<template lang="pug">
div(v-if='!error')
  Overlay(unique-key='yotpoWriteReviewModal' mode='LIGHT')
    LazyYotpoReviewsWriteReviewModal(
      :yotpo-id='data.yotpoId'
      :product-name='data.name'
      :product-line-slug='data.productLineSlug'
    )

  Overlay(v-if='currentReview' unique-key='yotpoReviewModal' modal-width='EXTRA_WIDE' mode='LIGHT')
    LazyYotpoReviewsReviewModal(:review='currentReview' :scroll-to='imageIndex')

  YotpoReviewsStats(
    :yotpo-id='data.yotpoId'
    :selected-rating='selectedRating'
    @on-get-stats='getStats'
    @on-rating-select='selectRating'
  )

  YotpoReviewsFilters(
    v-if='hasReviews'
    :yotpo-id='data.yotpoId'
    :selected-topics='selectedTopics'
    :selected-sort='selectedSort'
    :selected-rating='selectedRating'
    :with-media='withMedia'
    @on-select-topic='selectTopic'
    @on-sort-change='selectSort'
    @on-rating-change='selectRating'
    @on-media-change='selectMedia'
  )
    template(#search)
      div(class='mr-2 w-1/2 lg:w-44')
        SearchIcon(class='[&_path]:fill-gray ml-2.5 mt-2.5 w-4 h-4 absolute')
        input(
          v-model='searchQuery'
          aria-label='Search reviews'
          placeholder='Search reviews'
          class='w-full rounded-full border border-gray-light hover:border-black text-sm h-9 pl-8 placeholder:text-black'
        )

  div(v-if='hasFilters' class='border-t py-3')
    span(class='text-sm mr-2') We found {{ totalFilteredReviews }} matching reviews.
    button(aria-label='Clear filters' class='text-sm underline font-bold' @click='clearFilters') Clear filters

  div(v-if='!hasReviews')
    div(class='text-center')
      div(class='mt-10 lg:mt-2 mb-6 text-xl font-bold') Be the first to write a review!
      div(class='mb-6') Let us know what you think
      button(
        aria-label='Write a review'
        class='bg-black text-white text-sm font-bold rounded-full mb-16 w-52 h-10'
        @click='openWriteReviewModal'
      ) Write A Review

  div(v-else-if='reviews.length > 0' :class='{ "opacity-50": isLoading }')
    template(v-for='review in reviews' :key='review.id')
      YotpoReviewsReview(:review='review' @on-open-modal='openReviewModal')

    div(v-if='Math.ceil(totalFilteredReviews / 10) > page' class='flex justify-center')
      button(
        aria-label='Show more reviews'
        class='bg-black text-white text-sm font-bold rounded-full m-12 px-6 py-2'
        @click='loadMore'
      ) Show more reviews

  div(v-else class='pt-16 text-center border-t')
    div(class='mb-4 font-bold') No matching reviews
    div(class='text-sm mb-4') Try clearing or changing the filters.
    button(
      aria-label='Clear filters'
      class='bg-black text-white text-sm font-bold rounded-full m-12 w-32 h-8'
      @click='clearFilters'
    ) Clear filters
</template>

<script setup lang="ts">
import SearchIcon from '@/assets/search.svg?component'

const { $sitewideConfig, $overlay } = useNuxtApp()

const { data } = defineProps<{
  data: ProductLine
}>()

const error = ref(false)
const isLoading = ref(false)

const searchQuery = ref<string>('')
const selectedSort = ref('score')
const selectedRating = ref('all')
const selectedTopics = ref<string[]>([])
const selectedSorts = ref<YotpoSort[]>([
  { sort_by: 'score', ascending: false },
  { sort_by: 'date', ascending: false },
])

const bottomline = ref<YotpoBottomline | null>(null)
const totalFilteredReviews = ref(0)
const reviews = ref<YotpoReview[]>([])
const withMedia = ref<boolean>(false)
const page = ref(1)
const currentReview = ref<YotpoReview | null>(null)
const imageIndex = ref(0)

// Debounce search updates
watchDebounced(searchQuery, updateSearch, { debounce: 250, maxWait: 1500 })

onMounted(async () => {
  getReviews()
})

function loadMore() {
  page.value++
  getReviews(true)
}

async function getReviews(loadMore = false) {
  if (isLoading.value) return
  isLoading.value = true

  updateSort()
  try {
    const url = `https://api-cdn.yotpo.com/v1/reviews/${$sitewideConfig.config.yotpoApiKey}/filter.json?per_page=10&page=${page.value}`

    const res = await $fetch<YotpoReviewsResponse>(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: {
        domain_key: data.yotpoId,
        sortings: selectedSorts.value,
        ...(withMedia.value && { pictured: true }),
        ...(searchQuery.value && { free_text_search: searchQuery.value }),
        ...(selectedTopics.value.length > 0 && { topic_names: selectedTopics.value }),
        ...(selectedRating.value !== 'all' && { scores: [parseInt(selectedRating.value)] }),
      },
    })

    totalFilteredReviews.value = res.response.pagination.total
    // If we are loading more reviews, we need to append the new reviews to the existing reviews
    reviews.value = loadMore ? [...reviews.value, ...res.response.reviews] : res.response.reviews
  } catch (err) {
    //
  } finally {
    isLoading.value = false
  }
}

const hasFilters = computed(() => {
  return selectedTopics.value.length > 0 || selectedRating.value !== 'all' || withMedia.value || searchQuery.value
})

const hasReviews = computed(() => {
  return bottomline.value && bottomline.value.total_review > 0
})

function getStats(value: YotpoBottomline) {
  bottomline.value = value
}

function clearFilters() {
  selectedTopics.value = []
  selectedRating.value = 'all'
  searchQuery.value = ''
  withMedia.value = false
  selectedSort.value = ''
  page.value = 1
  getReviews()
}

function updateSearch() {
  getReviews()
}

function selectMedia(value: boolean) {
  withMedia.value = value
  getReviews()
}

function selectTopic(topic: string) {
  const index = selectedTopics.value.indexOf(topic)
  if (index > -1) selectedTopics.value.splice(index, 1)
  else selectedTopics.value.push(topic)
  getReviews()
}

function selectRating(value: string) {
  selectedRating.value = value
  getReviews()
}

function selectSort(value: string) {
  selectedSort.value = value
  getReviews()
}

function updateSort() {
  switch (selectedSort.value) {
    case 'score':
      selectedSorts.value = [
        { sort_by: 'score', ascending: false },
        { sort_by: 'date', ascending: false },
      ]
      break
    case 'low_score':
      selectedSorts.value = [
        { sort_by: 'score', ascending: true },
        { sort_by: 'date', ascending: false },
      ]
      break
    case 'with_media':
      withMedia.value = true
      selectedSorts.value = [{ sort_by: 'date', ascending: false }]
      break
    case 'date':
      selectedSorts.value = [{ sort_by: 'date', ascending: false }]
      break
    case 'reviewer_type':
      selectedSorts.value = [
        { sort_by: 'reviewer_type', ascending: false },
        { sort_by: 'image_count', ascending: false },
        { sort_by: 'date', ascending: false },
      ]
      break
  }
}

function openReviewModal(review: YotpoReview, index: number) {
  currentReview.value = review
  imageIndex.value = index
  $overlay.open('yotpoReviewModal')
}

function openWriteReviewModal() {
  $overlay.open('yotpoWriteReviewModal')
}
</script>
